import 'Components/vm-bottom-sheet/vm-bottom-sheet';
import 'Components/vm-button/vm-button';
import 'Components/vm-clamped-text/vm-clamped-text';
import 'Components/vm-icon/vm-icon';
import 'Components/vm-profile-image/vm-profile-image';
import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import 'Modules/kol-profile/components/vm-kol-profile-stat-box/vm-kol-profile-stat-box';
import '../../../../app/(main)/(content_sidebar_layout)/video/[slug]/_components/vm-video-autosize-bottom-sheet/vm-video-autosize-bottom-sheet';

import { trackLearnMoreClick } from 'Api/videos';
import type { Byline, Contributor } from 'Modules/video/components/vm-video-info/vm-video-info';
import Styles from './vm-video-info-bottom-sheet.scss';

@customElement('vm-video-info-bottom-sheet')
export class VmVideoInfoBottomSheet extends LitElement {
    static override styles = Styles;

    @property({ type: Boolean, reflect: true }) open = false;
    @property({ type: String }) videoTitle = '';
    @property({ type: String }) videoSlug = '';
    @property({ type: Number }) viewCount = 0;
    @property({ type: Number }) thumbsUpCount = 0;
    @property({ type: String }) publishedAgo = '';
    @property({ type: Array }) contributors: Contributor[] = [];
    @property({ type: Object }) byline: Byline | null = null;
    @property({ type: String }) description = '';
    @property({ type: String }) contentActionInfo = '';
    @property({ type: String }) contentActionUrl = '';
    @property({ type: String }) safetyInformation = '';

    private handleLearnMoreClick = () => {
        trackLearnMoreClick(this.videoSlug);
    };

    private renderDescriptionCard() {
        if (!this.description) return nothing;

        return html`
            <div class="vm-video-info-bottom-sheet__card">
                <vm-clamped-text class="vm-video-info-bottom-sheet__description"
                    >${unsafeHTML(this.description)}</vm-clamped-text
                >
            </div>
        `;
    }

    private renderSafetyInformation() {
        if (!this.safetyInformation) return nothing;

        return html`
            <vm-clamped-text class="vm-video-info-bottom-sheet__isi" lines="3">
                ${unsafeHTML(this.safetyInformation)}
            </vm-clamped-text>
        `;
    }

    private renderContributorCards() {
        if (this.contributors.length === 0) return nothing;

        return this.contributors.map(
            (contributor) => html`
                <div class="vm-video-info-bottom-sheet__card vm-video-info-bottom-sheet__card--contributor">
                    ${contributor.href
                        ? html`<a
                              class="vm-video-info-bottom-sheet__card-link-overlay"
                              href=${contributor.href}
                              aria-hidden="true"
                              tabindex="-1"
                          ></a>`
                        : nothing}
                    <vm-profile-image
                        class="vm-video-info-bottom-sheet__contributor-avatar"
                        size="sm"
                        .imageSrc=${contributor.avatar ?? ''}
                        .userName="${contributor.name ?? ''}"
                        fit="cover"
                    ></vm-profile-image>
                    <span class="vm-video-info-bottom-sheet__contributor-name">${contributor.name}</span>
                    ${contributor.href
                        ? html`<vm-button
                              class="vm-video-info-bottom-sheet__contributor-link"
                              variant="ghost"
                              color-scheme="neutral"
                              href=${contributor.href ?? ''}
                              icon-only
                              aria-label=${contributor.name ?? ''}
                              size="sm"
                              ><vm-icon name="arrow-narrow-right" slot="icon-leading"></vm-icon
                          ></vm-button>`
                        : nothing}
                </div>
            `,
        );
    }

    private renderBylineCard() {
        if (!this.byline?.name) return nothing;

        const header = html`
            <vm-profile-image
                class="vm-video-info-bottom-sheet__byline-avatar"
                size="md"
                .imageSrc="${this.byline.avatar ?? ''}"
                .userName="${this.byline.name}"
                fit="cover"
            ></vm-profile-image>
            <span class="vm-video-info-bottom-sheet__byline-name">${this.byline.name}</span>
            <vm-button
                class="vm-video-info-bottom-sheet__byline-link"
                variant="ghost"
                color-scheme="neutral"
                href=${this.byline.href ?? ''}
                icon-only
                aria-label=${this.byline.name}
                size="sm"
                ><vm-icon name="arrow-narrow-right" slot="icon-leading"></vm-icon
            ></vm-button>
        `;

        const overlayLink = this.byline.href
            ? html`<a
                  class="vm-video-info-bottom-sheet__card-link-overlay"
                  href=${this.byline.href}
                  aria-hidden="true"
                  tabindex="-1"
              ></a>`
            : nothing;

        if (!this.contentActionInfo) {
            return html`<div class="vm-video-info-bottom-sheet__card vm-video-info-bottom-sheet__card--byline">
                ${overlayLink}${header}
            </div>`;
        }

        return html`
            <div class="vm-video-info-bottom-sheet__card vm-video-info-bottom-sheet__card--with-description">
                <div class="vm-video-info-bottom-sheet__card-header">${overlayLink}${header}</div>
                <div class="vm-video-info-bottom-sheet__card-body">
                    <vm-clamped-text class="vm-video-info-bottom-sheet__description">
                        ${unsafeHTML(this.contentActionInfo)}
                    </vm-clamped-text>
                    ${this.contentActionUrl
                        ? html`<vm-button
                              class="vm-video-info-bottom-sheet__learn-more"
                              variant="ghost"
                              color-scheme="neutral"
                              href=${this.contentActionUrl}
                              size="md"
                              @click=${this.handleLearnMoreClick}
                              target="_blank"
                              rel="noopener noreferrer"
                              >Learn more</vm-button
                          >`
                        : nothing}
                </div>
            </div>
        `;
    }

    override render() {
        if (!this.byline?.name && this.contributors.length === 0 && !this.description && !this.safetyInformation)
            return nothing;

        const date = new Date(this.publishedAgo);
        const year = isNaN(date.getTime()) ? 0 : date.getFullYear();
        const monDay = isNaN(date.getTime())
            ? ''
            : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        return html`
            <vm-video-autosize-bottom-sheet .open=${this.open} disable-backdrop>
                <span slot="header">More info</span>
                ${this.videoTitle ? html`<p class="vm-video-info-bottom-sheet__title">${this.videoTitle}</p>` : nothing}
                ${this.renderSafetyInformation()}
                <div class="vm-video-info-bottom-sheet__stats">
                    <vm-kol-profile-stat-box .value=${this.thumbsUpCount} label="Thumbs up"></vm-kol-profile-stat-box>
                    <vm-kol-profile-stat-box .value=${this.viewCount} label="Views"></vm-kol-profile-stat-box>
                    <vm-kol-profile-stat-box .value=${year} label="${monDay}"></vm-kol-profile-stat-box>
                </div>
                ${this.renderDescriptionCard()} ${this.renderContributorCards()} ${this.renderBylineCard()}
            </vm-video-autosize-bottom-sheet>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-info-bottom-sheet': VmVideoInfoBottomSheet;
    }
}
